import _ from "lodash";
import * as types from "../../constants";
import { decryptPatientsData } from '../../utils/index.jsx';
import { isSlimSurvey, isFullSurvey } from '../../utils/slimSubmissions';
/**
 * Reducer that saves all the investigations loaded
 * @constructor
 * @param {boolean} state 
 */

 const initialState = {
    data: null,
    loading: false,
    error: null,
    nUpdatedRegisters : 0
}

function getSubmissions(uuidPatient, uuidSurvey, data){
    if(data && data[uuidPatient] && data[uuidPatient][uuidSurvey]){
        return data[uuidPatient][uuidSurvey].submissions;
    }
    return [];
}

function saveSubmissions(uuidPatient, uuidSurvey, submissions, data){
    if(data && data[uuidPatient] && data[uuidPatient][uuidSurvey]){
        data[uuidPatient][uuidSurvey].submissions = submissions;
    }
    return data;
}

 
export default function reducer(state = initialState, action){
    
    let tempData;
    let newState = { ...state};
    let dictSurveys = {};
    switch(action.type){
        case types.FETCH_SUBMISSIONS_PATIENT_SUCCESS: {
            // SHER-590: the patient page fires a SLIM metadata load and a FULL
            // load on mount, both dispatching this action, and they race. A slim
            // payload must NOT overwrite full submissions the store already holds
            // for a survey — otherwise a slim load resolving last wipes e.g. the
            // inpatient medication list, which only reappears once a create
            // triggers a fresh full load. A full payload still wins over an earlier
            // slim one (the slim→full upgrade). Shallow-clone so connect() sees a
            // new `data` ref (matches the SHER-495 branch); the patient dict is
            // still a full replace of the survey set.
            tempData = newState.data === initialState.data ? {} : { ...newState.data };
            const existingSurveys = tempData[action.meta.uuidPatient] || {};
            // SHER-590 (residual): both load thunks have an offline-tolerant catch
            // that dispatches this SUCCESS with `surveys: []` on a network-less
            // error (timeout / dropped connection / CORS — submissionsPatientActions
            // .js:42,111). With an empty payload the forEach below never runs and
            // the patient dict is replaced with {}, wiping already-loaded clinical
            // data (the inpatient medication list) until a later successful load —
            // exactly the intermittent "meds disappear until you add one" report.
            // An empty payload must never clobber surveys the store already holds;
            // settle loading/error and keep the existing data. A genuine empty FIRST
            // load (patient with no surveys yet) still falls through to the normal
            // empty replace, so that UX is unchanged.
            if (action.surveys.length === 0 && Object.keys(existingSurveys).length > 0) {
                newState.loading = initialState.loading;
                newState.error = initialState.error;
                return newState;
            }
            dictSurveys = {};
            action.surveys.forEach(sur => {
                const existing = existingSurveys[sur.uuid];
                dictSurveys[sur.uuid] = isSlimSurvey(sur) && isFullSurvey(existing) ? existing : sur;
            })

            tempData[action.meta.uuidPatient] = dictSurveys;
            newState.data = tempData;
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            return newState;
        }
        case types.FETCH_SUBMISSIONS_PATIENT_SURVEY_SUCCESS:
            // SHER-495: per-survey load. Unlike FETCH_SUBMISSIONS_PATIENT_SUCCESS, this MERGES
            // the fetched survey(s) into the patient's existing dict instead of replacing it —
            // so the slim list rows the patient page populated for the other surveys survive.
            // Fresh top-level + per-patient objects keep referential equality breaking for connect().
            tempData = newState.data === initialState.data ? {} : { ...newState.data };
            dictSurveys = { ...(tempData[action.meta.uuidPatient] || {}) };
            action.surveys.forEach(sur => {
                dictSurveys[sur.uuid] = sur;
            })
            tempData[action.meta.uuidPatient] = dictSurveys;
            newState.data = tempData;
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            return newState;
        case types.FETCH_SINGLE_SUBMISSIONS_PATIENT_SUCCESS:
            tempData = newState.data === initialState.data ? {} : newState.data;
            dictSurveys = {};
            if(tempData[action.meta.uuidPatient]){
                dictSurveys = {...tempData[action.meta.uuidPatient]};
            }
            if(dictSurveys[action.submission.uuidSurvey]){
                dictSurveys[action.submission.uuidSurvey].submissions.push(action.submission);
            }
            else{
                dictSurveys[action.submission.uuidSurvey] = {"submissions" : [action.submission]}; 
            }
            
            tempData[action.meta.uuidPatient] = dictSurveys;
            newState.data = tempData;
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            return newState;
        case types.INITIALIZE_SUBMISSION_PATIENT:
            tempData = {};
            dictSurveys[action.payload.uuidSurvey] = action.payload.surveyData;
            
            tempData[action.payload.uuidPatient] = dictSurveys;
            newState.data = tempData;
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            return newState;
        case types.SUBMISSIONS_PATIENT_LOADING:
            newState.loading = true;
            newState.error = initialState.error;
            return newState;
        case types.REMOVE_SUBMISSIONS_PATIENT: 
            // Create a new top-level data object to trigger React updates
            tempData = newState.data === initialState.data ? {} : {...newState.data};
            
            // Only modify data if the nested structure exists
            if(tempData && tempData[action.meta.uuidPatient] && tempData[action.meta.uuidPatient][action.meta.uuidSurvey]) {
                // Create new survey object with filtered submissions
                let tempDict = {...tempData[action.meta.uuidPatient][action.meta.uuidSurvey]};
                let tempSubmissions = [...tempDict.submissions];
            
                const findSumission = tempSubmissions.findIndex(sub => sub.id === action.meta.idSubmission);
                if(findSumission !== -1){
                    tempSubmissions.splice(findSumission, 1);
                }
                
                tempDict.submissions = tempSubmissions;
                
                // Create new patient object reference  
                tempData[action.meta.uuidPatient] = {
                    ...tempData[action.meta.uuidPatient],
                    [action.meta.uuidSurvey]: tempDict
                };
            }
            
            newState.data = tempData;
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            newState.nUpdatedRegisters++;
            return newState;
        case types.UPDATE_SUBMISSIONS_PATIENT_SUCCESS:
        case types.UPDATE_SUBMISSIONS_PATIENT_OFFLINE:
        case types.STOP_TREATMENT_SUBMISSIONS_PATIENT_SUCCESS:
        case types.STOP_TREATMENT_SUBMISSIONS_PATIENT_OFFLINE:
        case types.SAVE_SUBMISSIONS_PATIENT_OFFLINE:
        case types.SAVE_SUBMISSIONS_PATIENT_SUCCESS:
            // SHER-186/192 — shallow-clone every layer we touch so React-Redux's
            // referential-equality check sees the change and re-renders downstream
            // components (specifically the medical-history form's PDF preview).
            // Previously this branch mutated newState.data in place; the wrapping
            // newState reference changed but data, data[uuidPatient] and
            // data[uuidPatient][surveyUUID] kept their old references, so connect()
            // skipped the render and the PDF kept showing the pre-edit text.
            tempData = newState.data === initialState.data ? {} : {...newState.data};
            let tempDict = {};
            let tempSubmissions = [];
            //Si se genera en offline, no hay id
            if( _.isEmpty(tempData) || !tempData[action.meta.uuidPatient]){

                tempDict = {};
                tempDict[action.meta.surveyUUID] = {};
                tempDict[action.meta.surveyUUID]["submissions"] = [action.submission];
                tempData[action.meta.uuidPatient] = tempDict;

                newState.data = tempData;
                newState.loading = initialState.loading;
                newState.error = initialState.error;
                return newState;
            }
            else if(tempData[action.meta.uuidPatient].hasOwnProperty(action.meta.surveyUUID)){
                tempDict = {...tempData[action.meta.uuidPatient][action.meta.surveyUUID]};
                tempSubmissions = [...tempDict.submissions];
                if([types.UPDATE_SUBMISSIONS_PATIENT_OFFLINE, types.UPDATE_SUBMISSIONS_PATIENT_SUCCESS,
                    types.STOP_TREATMENT_SUBMISSIONS_PATIENT_SUCCESS, types.STOP_TREATMENT_SUBMISSIONS_PATIENT_OFFLINE].includes(action.type)){
                    const indexSub = tempDict.submissions.findIndex(sub => sub.id === action.meta.idSubmission);
                    if(indexSub !== -1){
                        tempSubmissions[indexSub] = action.submission;
                    } else {
                        tempSubmissions.push(action.submission);
                    }
                }
                else{
                    if(!action.submission.id){
                        action.submission.id =  Math.floor(Math.random() * 10000);
                        action.submission.offline = true;
                    }
                    if(action.meta.oldIdSubmission){
                        const indexOldSub = tempSubmissions.findIndex((sub) => {
                            return sub.id === action.meta.oldIdSubmission
                        })
                        if(indexOldSub !== -1){
                            tempSubmissions.splice(indexOldSub, 1)
                        }
                    }
                    tempSubmissions.push(action.submission);
                }
            }
            else{
                if(!action.submission.id){
                    action.submission.id = Math.floor(Math.random() * 10000 );
                    action.submission.offline = true;
                }
                tempDict = {
                    surveyName : action.meta.surveyName,
                    uuid : action.meta.surveyUUID,
                    type : action.meta.surveyType,
                }
                tempSubmissions.push(action.submission);
            }
            tempDict.submissions = tempSubmissions;

            // Clone the patient bucket too, otherwise data[uuidPatient] keeps the
            // old reference and selectors that read state.patientsSubmissions.data
            // see no change.
            tempData[action.meta.uuidPatient] = {
                ...tempData[action.meta.uuidPatient],
                [action.meta.surveyUUID]: tempDict,
            };
            newState.data = tempData;
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            if([types.SAVE_SUBMISSIONS_PATIENT_OFFLINE, types.UPDATE_SUBMISSIONS_PATIENT_OFFLINE].includes(action.type)){
                newState.error = 2;//Offline
            }
            else{
                newState.error = initialState.error;
            }
            
            newState.nUpdatedRegisters++ 
            
            return newState;

        case types.ADD_ADDENDUM_TO_SUBMISSION_SUCCESS: {
            // SHER-223 Phase 2: append the new addendum to the matching
            // submission's `addenda` array, with the same shallow-clone
            // discipline as the SUCCESS branch above so connect() sees the
            // change and re-renders the inline addenda list.
            tempData = newState.data === initialState.data ? {} : {...newState.data};
            const patientBucket = tempData[action.meta.uuidPatient];
            if (patientBucket && patientBucket[action.meta.surveyUUID]) {
                const surveyDict = {...patientBucket[action.meta.surveyUUID]};
                const submissions = [...(surveyDict.submissions || [])];
                const idx = submissions.findIndex(sub => sub.id === action.meta.idSubmission);
                if (idx !== -1) {
                    const updated = {
                        ...submissions[idx],
                        addenda: [...((submissions[idx].addenda) || []), action.addendum],
                    };
                    submissions[idx] = updated;
                    surveyDict.submissions = submissions;
                    tempData[action.meta.uuidPatient] = {
                        ...patientBucket,
                        [action.meta.surveyUUID]: surveyDict,
                    };
                    newState.data = tempData;
                }
            }
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            newState.nUpdatedRegisters++;
            return newState;
        }
        case types.FETCH_SUBMISSIONS_PATIENT_ERROR:
            newState.loading = initialState.loading;
            newState.error = true;
            return newState;
        case types.DELETE_SUBMISSIONS_PATIENT_ERROR:
            // SHER-537: a failed delete resets loading but must NOT set the fatal
            // `error` flag — patient/index.jsx blanks the whole view on it. The
            // deleting component shows the failure reason inline instead.
            newState.loading = initialState.loading;
            newState.error = initialState.error;
            return newState;
        case types.SAVE_SUBMISSIONS_PATIENT_ERROR:
            newState.loading = initialState.loading;
            newState.error = action.error || true;
            return newState;
        case types.PERMISSION_SUBMISSIONS_PATIENT_ERROR:
            newState.loading = initialState.loading;
            newState.error = 401;
            return newState;
        case types.SUBMISSIONS_PATIENT_RESET_ERROR:
            newState.error = initialState.error;
            return newState;
        case types.AUTH_SIGN_OUT:
            newState = {...initialState};
            return newState;
        default:
            return state;
    }
}